import type {
  ActionResult,
  ArchiveChatDetail,
  ArchiveConfirmRequest,
  ArchiveKind,
  ArchiveUploadResult,
  ArchiveView,
  BookingUpdate,
  MoneyUpdate,
  SitesUpdate,
  AssignRequest,
  AssignResponse,
  AssistantToggleRequest,
  AuthPasswordRequest,
  AuthVerifyResponse,
  BookingAction,
  BookingDetail,
  BookingsView,
  CalendarView,
  CantGoRequest,
  ChatView,
  ChatsView,
  DashboardView,
  DispatchUpdate,
  FreeGuide,
  FreeGuidesView,
  GuideDetail,
  GuideInput,
  GuidePayUpdate,
  GuidesView,
  KnowledgeCreate,
  KnowledgeEntryView,
  KnowledgeUpdate,
  KnowledgeView,
  LeadDetail,
  LeadsView,
  Me,
  NeedsActionId,
  NeedsReplyResult,
  NeedsYouView,
  PortalActionResult,
  PortalHome,
  PortalLang,
  PortalMonth,
  PortalTripDetail,
  PickupTimesUpdate,
  PricesUpdate,
  RulesUpdate,
  SendResult,
  SettingsView,
  SetupUpdate,
  SetupView,
  GuideSuggestion,
  HotelInput,
  HotelsView,
  SpendingUpdate,
  SuggestResult,
} from '@cc/api-types';
import { request, upload } from './http';

const get = <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal);
const post = <T>(path: string, body: unknown = {}) => request<T>('POST', path, body);
const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body);
const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body);

/** Needs-you actions that are a plain POST to /api/needs-you/:id/<slug>. */
export type NeedsPostAction = Extract<NeedsActionId, 'approve' | 'reply_myself' | 'keep_looking' | 'tell_client' | 'ask_another_day' | 'tell_full'>;
/** Booking actions that are a plain POST to /api/bookings/:id/<slug>. */
export type BookingPostAction = Extract<BookingAction['id'], 'ask_another_day' | 'tell_client' | 'weather_cancel' | 'refund_deposit'>;

const slug = (id: string) => id.replace(/_/g, '-');

/** The picker list; an older server sends a bare array with no team. */
function freeGuidesView(data: unknown): FreeGuidesView {
  if (Array.isArray(data)) return { guides: data as FreeGuide[], team: [], full: false };
  if (typeof data === 'object' && data !== null && Array.isArray((data as { guides?: unknown }).guides)) {
    const v = data as Partial<FreeGuidesView> & { guides: FreeGuide[] };
    return { guides: v.guides, team: v.team ?? [], full: v.full ?? false };
  }
  return { guides: [], team: [], full: false };
}

const lang = (l: PortalLang) => `?lang=${l}`;

export const api = {
  // session
  me: (signal?: AbortSignal) => get<Me>('/api/me', signal),
  signIn: (body: AuthPasswordRequest) => post<AuthVerifyResponse>('/api/auth/password', body),
  logout: () => post<unknown>('/api/auth/logout'),
  setAssistant: (body: AssistantToggleRequest) => post<unknown>('/api/assistant', body),

  // the dashboard and leads
  dashboard: (signal?: AbortSignal) => get<DashboardView>('/api/dashboard', signal),
  leads: (signal?: AbortSignal) => get<LeadsView>('/api/leads', signal),
  lead: (id: number, signal?: AbortSignal) => get<LeadDetail>(`/api/leads/${id}`, signal),
  closeLead: (id: number) => post<ActionResult>(`/api/leads/${id}/close`),
  resendDepositLink: (id: number) => post<ActionResult>(`/api/leads/${id}/resend-link`),

  // needs you
  needsYou: (signal?: AbortSignal) => get<NeedsYouView>('/api/needs-you', signal),
  needsAction: (id: number, action: NeedsPostAction) => post<ActionResult>(`/api/needs-you/${id}/${slug(action)}`),
  needsChange: (id: number, instruction: string) => post<ActionResult>(`/api/needs-you/${id}/change`, { instruction }),
  needsReply: (id: number, text: string) => post<NeedsReplyResult>(`/api/needs-you/${id}/reply`, { text }),

  // chats
  chats: (signal?: AbortSignal) => get<ChatsView>('/api/chats', signal),
  chat: (id: number, signal?: AbortSignal) => get<ChatView>(`/api/chats/${id}`, signal),
  takeOver: (id: number) => post<unknown>(`/api/chats/${id}/take-over`),
  handBack: (id: number) => post<unknown>(`/api/chats/${id}/hand-back`),
  send: (id: number, text: string, replyTo?: number) => post<SendResult>(`/api/chats/${id}/send`, { text, replyTo }),
  suggest: (id: number) => post<SuggestResult>(`/api/chats/${id}/suggest`),
  markPersonal: (id: number) => post<ActionResult>(`/api/chats/${id}/personal`),

  // bookings
  bookings: (signal?: AbortSignal) => get<BookingsView>('/api/bookings?scope=upcoming', signal),
  booking: (id: number, signal?: AbortSignal) => get<BookingDetail>(`/api/bookings/${id}`, signal),
  freeGuides: async (id: number, signal?: AbortSignal) => freeGuidesView(await get<unknown>(`/api/bookings/${id}/free-guides`, signal)),
  assign: (id: number, body: AssignRequest) => post<AssignResponse>(`/api/bookings/${id}/assign`, body),
  bookingAction: (id: number, action: BookingPostAction) => post<ActionResult>(`/api/bookings/${id}/${slug(action)}`),

  // calendar
  calendar: (week: string | undefined, signal?: AbortSignal) =>
    get<CalendarView>(week ? `/api/calendar?week=${encodeURIComponent(week)}` : '/api/calendar', signal),
  weatherCancel: (date: string) => post<ActionResult>('/api/calendar/weather-cancel', { date }),

  // guides
  guides: (signal?: AbortSignal) => get<GuidesView>('/api/guides', signal),
  guide: (id: number, signal?: AbortSignal) => get<GuideDetail>(`/api/guides/${id}`, signal),
  addGuide: (body: GuideInput) => post<unknown>('/api/guides', body),
  editGuide: (id: number, body: Partial<GuideInput>) => patch<unknown>(`/api/guides/${id}`, body),
  markPaid: (id: number) => post<ActionResult>(`/api/guides/${id}/mark-paid`),
  saveDaysOff: (id: number, dates: string[]) => put<{ result: string; dates: string[] }>(`/api/guides/${id}/days-off`, { dates }),
  cashReceived: (id: number, body: { amountUsd: number; note?: string }) => post<{ result: string; owesUsd: number }>(`/api/guides/${id}/received`, body),

  // settings
  settings: (signal?: AbortSignal) => get<SettingsView>('/api/settings', signal),
  setup: (signal?: AbortSignal) => get<SetupView>('/api/setup', signal),
  updateSetup: (id: string, body: SetupUpdate) => post<unknown>(`/api/setup/${id}`, body),
  hotels: (signal?: AbortSignal) => get<HotelsView>('/api/hotels', signal),
  addHotel: (body: HotelInput) => post<unknown>('/api/hotels', body),
  removeHotel: (name: string) => request<unknown>('DELETE', '/api/hotels', { name }),
  guideSuggestions: (signal?: AbortSignal) => get<GuideSuggestion[]>('/api/guides/suggestions', signal),
  savePrices: (body: PricesUpdate) => put<unknown>('/api/settings/prices', body),
  saveRules: (body: RulesUpdate) => put<unknown>('/api/settings/rules', body),
  saveDispatch: (body: DispatchUpdate) => put<unknown>('/api/settings/dispatch', body),
  saveGuidePay: (body: GuidePayUpdate) => put<unknown>('/api/settings/guide-pay', body),
  saveSpending: (body: SpendingUpdate) => put<unknown>('/api/settings/spending', body),
  saveBooking: (body: BookingUpdate) => put<unknown>('/api/settings/booking', body),
  saveSites: (body: SitesUpdate) => put<unknown>('/api/settings/sites', body),
  saveMoney: (body: MoneyUpdate) => put<unknown>('/api/settings/money', body),
  savePickupTimes: (body: PickupTimesUpdate) => put<unknown>('/api/settings/pickup-times', body),

  // what Keta knows
  knowledge: (signal?: AbortSignal) => get<KnowledgeView>('/api/knowledge', signal),
  addKnowledge: (body: KnowledgeCreate) => post<KnowledgeEntryView>('/api/knowledge', body),
  updateKnowledge: (id: number, body: KnowledgeUpdate) => patch<KnowledgeEntryView>(`/api/knowledge/${id}`, body),
  whatsappLiveFromNow: () => post<{ result: 'live'; since: string }>('/api/whatsapp/live-from-now'),

  // old WhatsApp chats
  archive: (kind: ArchiveKind | 'unsorted', offset: number, signal?: AbortSignal) =>
    get<ArchiveView>(`/api/archive?kind=${kind}&offset=${offset}`, signal),
  archiveChat: (id: number, signal?: AbortSignal) => get<ArchiveChatDetail>(`/api/archive/chats/${id}`, signal),
  archiveImport: () => post<ActionResult>('/api/archive/import'),
  archiveSort: (limit?: number) => post<ActionResult>('/api/archive/sort', limit ? { limit } : {}),
  archiveStop: () => post<ActionResult>('/api/archive/stop'),
  archiveConfirm: (body: ArchiveConfirmRequest) => patch<unknown>('/api/archive/chats', body),
  archiveUpload: (files: File[]) => {
    const form = new FormData();
    for (const f of files) form.append('files', f, f.name);
    return upload<ArchiveUploadResult>('/api/archive/upload', form);
  },

  // guide portal (public, the token is the key)
  portal: {
    home: (token: string, l: PortalLang, signal?: AbortSignal) => get<PortalHome>(`/api/g/${encodeURIComponent(token)}${lang(l)}`, signal),
    trip: (token: string, assignmentId: number, l: PortalLang, signal?: AbortSignal) =>
      get<PortalTripDetail>(`/api/g/${encodeURIComponent(token)}/trips/${assignmentId}${lang(l)}`, signal),
    month: (token: string, l: PortalLang, signal?: AbortSignal) => get<PortalMonth>(`/api/g/${encodeURIComponent(token)}/month${lang(l)}`, signal),
    accept: (token: string, assignmentId: number, l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/offers/${assignmentId}/accept${lang(l)}`),
    decline: (token: string, assignmentId: number, l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/offers/${assignmentId}/decline${lang(l)}`),
    cantGo: (token: string, assignmentId: number, body: CantGoRequest, l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/trips/${assignmentId}/cant-go${lang(l)}`, body),
    done: (token: string, assignmentId: number, l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/trips/${assignmentId}/done${lang(l)}`),
    confirm: (token: string, assignmentId: number, l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/trips/${assignmentId}/confirm${lang(l)}`),
    proposeOffer: (token: string, assignmentId: number, times: string[], l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/offers/${assignmentId}/propose${lang(l)}`, { times }),
    proposeTrip: (token: string, assignmentId: number, times: string[], l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/trips/${assignmentId}/propose${lang(l)}`, { times }),
    answer: (token: string, buttonId: string, l: PortalLang) =>
      post<PortalActionResult>(`/api/g/${encodeURIComponent(token)}/answer${lang(l)}`, { buttonId }),
  },
};
